import { PaymentMethod } from '../hooks/useCheckout';
import { PaymentSettings } from '@/hooks/usePaymentSettings';

interface Props {
  paymentMethod: PaymentMethod;
  onChange: (m: PaymentMethod) => void;
  onBack: () => void;
  onNext: () => void;
  loading: boolean;
  error: string;
  paymentError?: string;
  settings: PaymentSettings;
  codLimitExceeded?: boolean;
  codUpperLimit?: number;
}

export default function PaymentStep({ paymentMethod, onChange, onBack, onNext, loading, error, paymentError, settings, codLimitExceeded = false, codUpperLimit = 0 }: Props) {
  const options = [
    settings.kredi_karti_aktif && {
      value: 'card' as PaymentMethod,
      icon: 'ri-bank-card-line',
      label: 'Kredi / Banka Kartı',
      desc: 'iyzico güvencesiyle güvenli ödeme',
      disabled: false,
    },
    settings.havale_aktif && {
      value: 'bank' as PaymentMethod,
      icon: 'ri-bank-line',
      label: 'Havale / EFT',
      desc: 'Banka hesap bilgileri siparişle gönderilecek',
      disabled: false,
    },
    settings.kapida_odeme_aktif && {
      value: 'cash' as PaymentMethod,
      icon: 'ri-cash-line',
      label: 'Kapıda Ödeme',
      desc: codLimitExceeded
        ? `${codUpperLimit.toLocaleString('tr-TR')}₺ üzeri siparişlerde kullanılamaz`
        : settings.kapida_odeme_ucreti > 0
          ? `+${settings.kapida_odeme_ucreti}₺ hizmet bedeli`
          : 'Ücretsiz kapıda ödeme',
      disabled: codLimitExceeded,
    },
  ].filter(Boolean) as { value: PaymentMethod; icon: string; label: string; desc: string; disabled: boolean }[];

  const activeBanks = settings.banka_hesaplari.filter(b => b.aktif);

  return (
    <div className="bg-white rounded-2xl p-6">
      <h2 className="font-serif text-xl font-semibold text-coffee mb-6">Ödeme Yöntemi</h2>

      {options.length === 0 ? (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-700 mb-6">
          Şu an aktif ödeme yöntemi bulunmuyor. Lütfen daha sonra tekrar deneyin.
        </div>
      ) : (
        <div className="space-y-3 mb-6">
          {options.map(opt => (
            <label
              key={opt.value}
              className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
                opt.disabled
                  ? 'border-cream-dark bg-cream/50 opacity-50 cursor-not-allowed'
                  : paymentMethod === opt.value
                    ? 'border-gold bg-gold/5 cursor-pointer'
                    : 'border-cream-dark hover:border-coffee-light cursor-pointer'
              }`}
            >
              <input
                type="radio"
                name="payment"
                value={opt.value}
                checked={paymentMethod === opt.value}
                onChange={() => !opt.disabled && onChange(opt.value)}
                disabled={opt.disabled}
                className="sr-only"
              />
              <div
                className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${
                  opt.disabled
                    ? 'bg-cream text-coffee-light'
                    : paymentMethod === opt.value
                      ? 'bg-gold text-white'
                      : 'bg-cream text-coffee-light'
                }`}
              >
                <i className={`${opt.icon} text-lg`} />
              </div>
              <div className="flex-1">
                <p className="font-medium text-coffee text-sm">{opt.label}</p>
                <p className={`text-xs ${opt.disabled ? 'text-red-400' : 'text-coffee-light'}`}>{opt.desc}</p>
              </div>
              {opt.disabled ? (
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-lock-line text-coffee-light text-base" />
                </div>
              ) : paymentMethod === opt.value && (
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-checkbox-circle-line text-gold text-xl" />
                </div>
              )}
            </label>
          ))}
        </div>
      )}

      {/* Havale bilgileri */}
      {paymentMethod === 'bank' && settings.havale_aktif && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <div className="flex items-center gap-2 mb-3">
            <i className="ri-information-line text-amber-600" />
            <p className="text-sm font-semibold text-amber-800">Havale / EFT Bilgileri</p>
          </div>

          {activeBanks.length > 0 ? (
            <div className="space-y-3">
              {activeBanks.map(bank => (
                <div key={bank.id} className="bg-white rounded-lg p-3 border border-amber-100">
                  <p className="text-xs font-semibold text-coffee mb-1">{bank.banka_adi}</p>
                  {bank.sube_adi && <p className="text-xs text-coffee-light mb-1">{bank.sube_adi}</p>}
                  <p className="text-xs text-coffee mb-1">
                    <span className="text-coffee-light">Hesap Sahibi: </span>
                    {bank.hesap_sahibi}
                  </p>
                  <p className="text-xs font-mono text-coffee tracking-wide">{bank.iban}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-amber-700">Banka hesap bilgileri sipariş onayı ile gönderilecektir.</p>
          )}

          {settings.havale_talimatlari && (
            <p className="text-xs text-amber-700 mt-3 border-t border-amber-200 pt-3">
              {settings.havale_talimatlari}
            </p>
          )}
        </div>
      )}

      {paymentError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600 flex items-center gap-2">
          <i className="ri-error-warning-line flex-shrink-0" />
          {paymentError}
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
          {error}
        </div>
      )}

      <div className="flex gap-3">
        <button
          onClick={onBack}
          disabled={loading}
          className="flex-1 py-3.5 border border-coffee-light text-coffee font-medium rounded-xl hover:bg-cream transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
        >
          ← Geri
        </button>
        <button
          onClick={onNext}
          disabled={loading || options.length === 0}
          className="flex-1 py-3.5 bg-coffee hover:bg-coffee-medium text-white font-semibold rounded-xl transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <i className="ri-loader-4-line animate-spin" />
              <span>İşleniyor...</span>
            </>
          ) : (
            'Siparişi Tamamla →'
          )}
        </button>
      </div>
    </div>
  );
}
